import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import { priorityStyle } from "@/lib/taskPriority";
import Modal from "@/components/Modal";
import { formatDate } from "@/lib/dateUtils";

const STATUS_TONE = {
  pending: "bg-slate-100 text-slate-800 border-slate-300",
  in_progress: "bg-blue-100 text-blue-900 border-blue-300",
  completed: "bg-emerald-100 text-emerald-900 border-emerald-300",
};

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-3 py-1 text-sm">
      <div className="col-span-1 font-mono-tech text-[11px] tracking-wider uppercase text-[var(--text-secondary)]">
        {label}
      </div>
      <div className="col-span-2 font-display break-words">{value || "—"}</div>
    </div>
  );
}

export default function TaskDetailsModal({ open, task, onClose, onOpenOrder, onCompleted }) {
  const { t, lang } = useI18n();
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setOrder(null);
    if (!open || !task?.order_id) return;
    setLoadingOrder(true);
    axios
      .get(`${API}/orders`)
      .then(({ data }) => {
        if (cancelled) return;
        const found = (data || []).find((o) => o.id === task.order_id) || null;
        setOrder(found);
      })
      .catch(() => setOrder(null))
      .finally(() => !cancelled && setLoadingOrder(false));
    return () => {
      cancelled = true;
    };
  }, [open, task?.order_id]);

  if (!task) return null;

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await axios.post(`${API}/tasks/${task.id}/complete`);
      toast.success(t("completed"));
      onCompleted && onCompleted({ ...task, status: "completed" });
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setCompleting(false);
    }
  };

  const handleOpenOrder = () => {
    if (!order) return;
    onOpenOrder && onOpenOrder(order);
  };

  const isCompleted = task.status === "completed";
  const prio = task.priority || "normal";
  const ps = priorityStyle(prio);
  const orderLabel = order
    ? `#${order.invoice_number || "—"} · ${order.company_name || ""}`
    : task.order_id
    ? "—"
    : t("no_order_linked");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${t("task_details")} · ${task.name || ""}`}
      testId={DASHBOARD.taskDetailsModal}
      wide
    >
      <div className="space-y-4">
        {prio !== "normal" && (
          <div
            data-testid={DASHBOARD.taskPriorityBadge(task.id)}
            className={`border-2 px-4 py-2 font-display font-black tracking-widest text-sm ${ps.chip}`}
          >
            {prio === "urgent" ? "🔴 " : "🟡 "}
            {lang === "es" ? ps.labelEs : ps.label}
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] pb-3">
          <div>
            <div className="label-overline text-[var(--text-secondary)]">{t("due_date")}</div>
            <div className="font-display text-2xl font-black tracking-tight">
              {formatDate(task.due_date, lang)}
            </div>
          </div>
          <span
            className={`border px-3 py-1 font-display font-bold tracking-wide text-xs ${
              STATUS_TONE[task.status] || "bg-slate-100 border-slate-300"
            }`}
          >
            {t(`task_status_${task.status}`)}
          </span>
        </div>

        <div className="border border-[var(--border)] bg-white p-4">
          <Row label={t("task_name")} value={task.name} />
          <Row label={t("task_type")} value={task.task_type} />
          <Row label={t("customer")} value={order?.customer_name || order?.company_name} />
          <Row
            label={t("related_order")}
            value={loadingOrder ? "…" : orderLabel}
          />
          <Row label={t("task_description")} value={task.product} />
          <Row label={t("assigned_to")} value={task.employee_name || t("unassigned")} />
          <Row label={t("due_date")} value={formatDate(task.due_date, lang)} />
          <Row label={t("status")} value={t(`task_status_${task.status}`)} />
          <Row label={t("priority")} value={t(`priority_${prio}`)} />
          <Row label={t("notes")} value={order?.notes} />
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button
            data-testid="task-details-close"
            onClick={onClose}
            className="border border-[var(--border)] px-4 py-2 font-display font-bold hover:bg-[var(--surface-alt)]"
          >
            {t("close")}
          </button>
          <button
            data-testid={DASHBOARD.taskDetailsOpenOrder}
            onClick={handleOpenOrder}
            disabled={!order}
            className="border border-black bg-white px-4 py-2 font-display font-bold hover:bg-black hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {t("open_order")}
          </button>
          {!isCompleted && (
            <button
              data-testid={DASHBOARD.taskDetailsComplete(task.id)}
              onClick={handleComplete}
              disabled={completing}
              className="bg-[var(--text-primary)] text-white px-4 py-2 font-display font-bold hover:bg-[var(--primary)] disabled:opacity-50"
            >
              {completing ? "..." : t("mark_completed")}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
